import {
	inject,
	Component,
	computed,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { IAssetBuilding } from '../../../pages/settings/portfolio/interfaces/area-assets.interfaces';
import { IBuildingAddress } from '@app/pages/dashboard/interfaces/building.interfaces';

@Component({
	selector: 'editLocationDialog',
	templateUrl: './edit-location-dialog.component.html',
	styleUrl: './edit-location-dialog.component.scss',
	imports: [
		ReactiveFormsModule,
		MatInputModule,
		MatIconModule,
		MatFormFieldModule,
		MatSelectModule,
		MatButtonModule,
		NgxTolgeeModule,
		MatAutocompleteModule,
		MatButtonToggleModule,
		MatDatepickerModule,
		MatDialogModule,
	],
})
export class EditLocationDialogComponent {
	private readonly fb = inject(FormBuilder);
	private readonly data = inject(MAT_DIALOG_DATA) as {
		building: IAssetBuilding;
	};

	address: IBuildingAddress = this.data.building.address;
	isSavingProcess: boolean;
	locationForm = this.fb.group({
		name: [this.data.building.name, Validators.required],
		squareMeters: [this.data.building.buildingSquareMeters],
		constructionYear: [this.data.building.constructionYear],
		facilityType: [this.data.building.facilityType, Validators.required],
	});
	searchInput: WritableSignal<string> = signal('');
	allYears: WritableSignal<number[]> = signal(
		Array.from(
			{ length: new Date().getFullYear() },
			(_, i) => new Date().getFullYear() - i
		)
	);
	filteredYears: Signal<number[]> = computed(() =>
		this.allYears().filter(year => year.toString().includes(this.searchInput()))
	);

	limitYearInput(event: Event) {
		const target = event.target as HTMLInputElement;
		if (target.value.length > 3) {
			return false;
		}
	}

	goBack() {
		close();
	}

	isInvalidForm() {
		const nameControl = this.locationForm.controls.name;
		const typeControl = this.locationForm.controls.facilityType;
		const originalBuilding = this.data.building;
		const formValue = this.locationForm.value;

		const unchanged =
			formValue.name === originalBuilding.name &&
			formValue.facilityType === originalBuilding.facilityType &&
			formValue.squareMeters === originalBuilding.buildingSquareMeters &&
			formValue.constructionYear === originalBuilding.constructionYear;

		return (
			nameControl.hasError('required') || typeControl.hasError('required') || unchanged
		);
	}

	onInput(event: Event) {
		this.searchInput.set((event.target as HTMLInputElement).value);
	}

	saveChanges(): IAssetBuilding {
		if (!this.locationForm.valid) {
			return null;
		}
		return {
			...this.data.building,
			name: this.locationForm.value.name,
			facilityType: this.locationForm.value.facilityType,
			buildingSquareMeters: this.locationForm.value.squareMeters,
			constructionYear: this.locationForm.value.constructionYear,
		};
	}
}
